// src/pages/Users/Galleries/components/AlbumLightbox.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaExternalLinkAlt,
  FaTimes,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";
import {
  FALLBACK_ALBUM_IMG,
  stripHtml,
} from "../utils/galleriesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_ALBUM_IMG;
  }
}

export default function AlbumLightbox({
  isOpen,
  photos = [],
  startIndex = 0,
  onClose,
}) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    if (!isOpen) return;
    setIndex(startIndex);
  }, [isOpen, startIndex]);

  const current = photos[index] || {};

  const currentSrc = useMemo(
    () => toAbsolutePhotoUrl(current?.photo_url || "") || FALLBACK_ALBUM_IMG,
    [current?.photo_url]
  );

  const caption = stripHtml(current?.description || "");

  const previous = useCallback(() => {
    setIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (event.key === "ArrowLeft" && photos.length > 1) previous();
      if (event.key === "ArrowRight" && photos.length > 1) next();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, next, onClose, photos.length, previous]);

  useEffect(() => {
    if (!isOpen || photos.length <= 1) return;

    const preload = (targetIndex) => {
      const photo = photos[targetIndex];
      if (!photo?.photo_url) return;

      const img = new Image();
      img.src = toAbsolutePhotoUrl(photo.photo_url);
    };

    preload((index + 1) % photos.length);
    preload((index - 1 + photos.length) % photos.length);
  }, [index, isOpen, photos]);

  if (!isOpen || photos.length === 0) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/90 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse photo"
      onClick={onClose}
    >
      <div
        className="flex h-full flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 text-white">
          <div className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold backdrop-blur">
            {index + 1} / {photos.length}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 active:scale-95"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        <div className="relative flex flex-1 items-center justify-center py-4">
          {photos.length > 1 && (
            <button
              type="button"
              onClick={previous}
              className="absolute left-0 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 active:scale-95 sm:left-4"
              aria-label="Photo précédente"
            >
              <FaChevronLeft />
            </button>
          )}

          <div className="w-full max-w-6xl">
            <img
              src={currentSrc}
              alt={caption || `Photo ${index + 1}`}
              className="mx-auto max-h-[72dvh] w-full rounded-[1.5rem] object-contain shadow-2xl"
              onError={onImgError}
            />

            {caption && (
              <p className="mx-auto mt-3 max-w-3xl rounded-2xl bg-white/10 px-4 py-3 text-sm leading-6 text-white backdrop-blur">
                {caption}
              </p>
            )}
          </div>

          {photos.length > 1 && (
            <button
              type="button"
              onClick={next}
              className="absolute right-0 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition hover:bg-white/20 active:scale-95 sm:right-4"
              aria-label="Photo suivante"
            >
              <FaChevronRight />
            </button>
          )}
        </div>

        <footer className="flex justify-end gap-2">
          <a
            href={currentSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 text-sm font-bold text-white backdrop-blur transition hover:bg-white/20 active:scale-95"
          >
            <FaExternalLinkAlt />
            <span className="hidden sm:inline">Original</span>
          </a>

          <a
            href={currentSrc}
            download
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-white px-4 text-sm font-bold text-slate-950 transition hover:bg-slate-100 active:scale-95"
          >
            <FaDownload />
            <span className="hidden sm:inline">Télécharger</span>
          </a>
        </footer>
      </div>
    </div>,
    document.body
  );
}